import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useSearch } from '@/hooks/useSearch'
import { ConfidenceBadge } from '@/components/shared/ConfidenceBadge'
import { WikidataLink } from '@/components/shared/WikidataLink'
import { Badge } from '@brent/core-ui/ui'
import { Input } from '@brent/core-ui/ui'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@brent/core-ui/ui'
import { SearchIcon } from 'lucide-react'
import type { Composer, Work, Performer, Concert, Venue } from '@brent/shared/types'

export function Search() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const urlQuery = searchParams.get('q') || ''
  const [inputValue, setInputValue] = useState(urlQuery)
  const { data, isLoading, error } = useSearch(urlQuery)

  // Sync input when URL query changes (e.g. from header search)
  useEffect(() => {
    setInputValue(urlQuery)
  }, [urlQuery])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = inputValue.trim()
    if (trimmed.length >= 2) {
      setSearchParams({ q: trimmed })
    }
  }

  const totalResults = data
    ? data.composers.length +
      data.works.length +
      data.performers.length +
      data.concerts.length +
      data.venues.length
    : 0

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Search</h1>
        <p className="text-muted-foreground mt-2">
          Search across composers, works, performers, concerts, and venues.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by name, title, or Wikidata QID..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="pl-10"
            autoFocus
          />
        </div>
      </form>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Searching...</div>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6">
          <h2 className="font-semibold text-destructive">Search failed</h2>
          <p className="mt-1 text-sm text-muted-foreground">{error.message}</p>
        </div>
      )}

      {data && !isLoading && (
        <div className="space-y-8">
          <p className="text-sm text-muted-foreground">
            {totalResults} result{totalResults !== 1 ? 's' : ''} for &ldquo;{urlQuery}&rdquo;
          </p>

          {totalResults === 0 && (
            <p className="text-muted-foreground py-8 text-center">
              No results found. Try a different search term.
            </p>
          )}

          {data.composers.length > 0 && (
            <SearchSection title="Composers" count={data.composers.length}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Confidence</TableHead>
                    <TableHead>Wikidata</TableHead>
                    <TableHead className="text-right">Works</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.composers.map((c: Composer) => (
                    <TableRow
                      key={c.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/composers/${c.id}`)}
                    >
                      <TableCell className="font-medium">{c.name_canonical}</TableCell>
                      <TableCell>
                        <ConfidenceBadge confidence={c.confidence} qualityScore={c.quality_score} />
                      </TableCell>
                      <TableCell>
                        <WikidataLink qid={c.wikidata_id} label={c.wikidata_label ?? undefined} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{c.programme_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </SearchSection>
          )}

          {data.works.length > 0 && (
            <SearchSection title="Works" count={data.works.length}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Composer</TableHead>
                    <TableHead>Wikidata</TableHead>
                    <TableHead className="text-right">Performances</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.works.map((w: Work) => (
                    <TableRow
                      key={w.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/works/${w.id}`)}
                    >
                      <TableCell className="font-medium">{w.title_canonical}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {w.composer_name || '-'}
                      </TableCell>
                      <TableCell>
                        <WikidataLink qid={w.wikidata_id} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{w.programme_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </SearchSection>
          )}

          {data.performers.length > 0 && (
            <SearchSection title="Performers" count={data.performers.length}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Instrument / Voice</TableHead>
                    <TableHead>Wikidata</TableHead>
                    <TableHead className="text-right">Appearances</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.performers.map((p: Performer) => (
                    <TableRow
                      key={p.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/performers/${p.id}`)}
                    >
                      <TableCell className="font-medium">{p.name_canonical}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {p.instrument_or_voice || '-'}
                      </TableCell>
                      <TableCell>
                        <WikidataLink qid={p.wikidata_id} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{p.appearances}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </SearchSection>
          )}

          {data.concerts.length > 0 && (
            <SearchSection title="Concerts" count={data.concerts.length}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Venue</TableHead>
                    <TableHead>Ensemble</TableHead>
                    <TableHead className="text-right">Items</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.concerts.map((c: Concert) => (
                    <TableRow
                      key={c.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/concerts/${c.id}`)}
                    >
                      <TableCell className="font-medium">{c.date_text}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {c.venue_name || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {c.ensemble_name || '-'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{c.programme_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </SearchSection>
          )}

          {data.venues.length > 0 && (
            <SearchSection title="Venues" count={data.venues.length}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Quality</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.venues.map((v: Venue) => (
                    <TableRow
                      key={v.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/venues/${v.id}`)}
                    >
                      <TableCell className="font-medium">{v.name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="capitalize">
                          {v.quality_score.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {v.notes || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </SearchSection>
          )}
        </div>
      )}
    </div>
  )
}

function SearchSection({
  title,
  count,
  children,
}: {
  title: string
  count: number
  children: React.ReactNode
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">
        {title} <span className="text-muted-foreground font-normal">({count})</span>
      </h2>
      <div className="rounded-md border">{children}</div>
    </section>
  )
}
